/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable camelcase */
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { getSelectedCustomer } from 'reducers/customerSearch';
import { Customer } from 'types/Customer';
import { SaleDocumentResponse } from 'types/SalesDocument';
import {
  getTotal,
  getTotalNet,
  getTotalTax,
  getProductsInShoppingCart,
  getVisibleTotalDiscount,
  getLastProductIndex,
  getShoppingCartForSalesDocument,
} from 'reducers/ShoppingCart';
import { getCurrentSalesDocument } from 'reducers/sales';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getSelectedPosID } from 'reducers/PointsOfSale';
import { getCurrencySymbol } from 'reducers/configs/settings';
import { CartItem } from 'containers/Forms/GiftCardSerial/types';
import { RootState } from 'reducers';

import {
  CDProductCartItem,
  CDGetCart,
  CDData,
  CDPayload,
  CDPromotionRule,
} from './types';

type ManualDiscount = {
  amount: number;
  finalPrice: number;
  manualDiscountPercentage: number;
  manualDiscountReasonID: number;
  totalDiscount: number;
};

type CampaignDiscount = {
  amount: number;
  campaignDiscountPercentage: number;
  campaignDiscountValue: number;
  campaignID: number;
  campaignType: 'ITEMS' | 'INVOICE';
  finalPrice: number;
  name: string;
  totalDiscount: number;
};

type PriceList = {
  amount: number;
  finalPrice: number;
  manualDiscountPercentage: number;
  manualDiscountReasonID: undefined;
  name: string;
  priceListDiscountPercentage: number;
  priceListDiscountType: string;
  priceListID: number;
  totalDiscount: number;
};

type Promotion = ManualDiscount | CampaignDiscount | PriceList;

function isCampaignDiscount(promo: Promotion): promo is CampaignDiscount {
  return 'campaignID' in promo && 'campaignType' in promo;
}

function isPriceList(promo: Promotion): promo is PriceList {
  return 'priceListID' in promo && 'priceListDiscountType' in promo;
}

function createDisplayPromotion(promo: Promotion): CDPromotionRule {
  if (isCampaignDiscount(promo)) {
    return {
      amount: promo.amount,
      campaignId: promo.campaignID,
      campaignType: promo.campaignType,
      campaignDiscountValue: promo.campaignDiscountValue,
      campaignDiscountPercentage: promo.campaignDiscountPercentage,
      // priceListId: 0,
      // priceListDiscountType: '',
      // priceListDiscountPercentage: 0,
      // manualDiscountPercentage: 0,
      name: promo.name,
      finalPrice: promo.finalPrice,
      totalDiscount: promo.totalDiscount,
    };
  }
  if (isPriceList(promo)) {
    return {
      amount: promo.amount,
      // campaignId: 0,
      // campaignType: '',
      // campaignDiscountValue: 0,
      // campaignDiscountPercentage: 0,
      priceListId: promo.priceListID,
      priceListDiscountType: promo.priceListDiscountType,
      priceListDiscountPercentage: promo.priceListDiscountPercentage,
      manualDiscountPercentage: promo.manualDiscountPercentage,
      name: promo.name,
      finalPrice: promo.finalPrice,
      totalDiscount: promo.totalDiscount,
    };
  }
  return {
    amount: promo.amount,
    // campaignId: 0,
    // campaignType: '',
    // campaignDiscountValue: 0,
    // campaignDiscountPercentage: 0,
    // priceListId: 0,
    // priceListDiscountType: '',
    // priceListDiscountPercentage: 0,
    manualDiscountPercentage: promo.manualDiscountPercentage,
    // name: '',
    finalPrice: promo.finalPrice,
    totalDiscount: promo.totalDiscount,
  };
}

function generateProductCartItem(item: CartItem & { promotions: Promotion[] }) {
  const promotionRules: CDPromotionRule[] = item.promotions.map(
    createDisplayPromotion,
  );
  const p: CDProductCartItem = {
    rowId: item.productID,
    productId: item.productID,
    amount: Number(item.amount),
    originalPrice: Number(item.basePrice),
    originalPriceWithVat: Number(item.basePriceWithVAT),
    price: Number(item.finalPrice),
    priceWithVat: Number(item.finalPriceWithVAT),
    promotionDiscount: item.promotionDiscount,
    manualDiscount: Number(item.manualDiscount),
    // TODO find source for the value
    // manualDiscountReasonCodeId: null,
    // TODO waiting for definition of for the field
    // manualDiscountExclusive: false,
    discount: item.totalDiscount,
    netTotal: Number(item.finalPrice),
    vatTotal: Number(item.rowVATTotal),
    totalWithVat: Number(item.rowTotal),
    taxRateId: item.vatrateID,
    data: {
      emptyCart: 'no',
      product_name: item.name,
      code8: item.code,
    },
    vatRate: Number(item.vatRate),
    promotionRules,
    // manualTaxRateId: null,
    // manualPrice: null,
    // returnReasonId: null,
  };
  return p;
}

function generateCustomerData(customer: Customer) {
  const c: CDData = {
    customer_name: customer.fullName,
    customer_email: customer.email,
    customer_number: customer.customerCardNumber,
    // contact_id: customer.id, // unknown info
    code: customer.code,
    group: customer.groupName,
    mobile: customer.mobile,
    address: customer.address,
    loyaltyCardNumber: customer.customerCardNumber,
    creditLimit: customer.creditLimit,
    rewardPoints: customer.rewardPoints,
    storeCredit: customer.availableCredit,
    employerLoyaltyCardNr: '', // marked as required - where do i get this?
    employerCode: '', // marked as required - where do i get this?
    employerStoreCredit: 0, // marked as required - where do i get this?
    employerName: customer.employerName,
    isCompany: customer.companyID ? 'true' : 'false',
  };
  return c;
}

function generateCartData() {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    const state = getState();
    const total = getTotal(state);
    const net = getTotalNet(state);
    const tax = getTotalTax(state);
    const productsInCart = getProductsInShoppingCart(state);
    const saleDocCart = getShoppingCartForSalesDocument(state);
    const customer: Customer = getSelectedCustomer(getState());
    const saleDoc: SaleDocumentResponse = getCurrentSalesDocument(state);
    const lastOrderIndex = getLastProductIndex(state);

    const cd: Omit<CDGetCart, 'cartId'> = {
      customerId: customer.customerID,
      warehouseId: getSelectedWarehouseID(state),
      pointOfSaleId: getSelectedPosID(state),
      documentType: saleDoc.type || 'CASHINVOICE',
      documentID: saleDoc.id,
      notes: saleDoc.notes,
      internalNotes: saleDoc.internalNotes,
      rows: productsInCart.map((p, i) => {
        const rowToModify = { ...p, promotions: saleDocCart[i].promotionRules };
        return generateProductCartItem(rowToModify);
      }),
      netTotal: net,
      vatTotal: tax,
      totalWithVat: total,
      originalNetTotal: net,
      originalTotalWithVat: total,
      data: generateCustomerData(customer),
      manualDiscount: getVisibleTotalDiscount(state),
      // rowIdentifier: 'productId', // possibly there are unique list of options "productId",
      nextRowId: lastOrderIndex + 1,
    };
    return cd;
  };
}

export function generatePayload() {
  return async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    const state = getState();
    const symbol = getCurrencySymbol(state);
    const payload: CDPayload = {
      getCart: await dispatch(generateCartData()),
      currencyPrefix: symbol,
      currencySuffix: symbol,
      warehouseId: getSelectedWarehouseID(state),
      posId: getSelectedPosID(state),
    };
    return JSON.stringify(payload);
  };
}
